import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Resets scroll on navigation, but honours a #fragment. The browser's own fragment
 * scroll runs before the SPA renders the page, so on a fresh load /features#desktop
 * stayed at the top (#897).
 *
 * Back/forward (POP) after the first load keeps the old behaviour so the browser's
 * scroll restoration wins over the anchor. "First load" is tracked by whether any
 * navigation has happened yet, not by location.key: the first entry's key is the
 * constant "default", so Back to it would look like a fresh load. A ref also survives
 * StrictMode's double-run of effects, which a plain boolean flip would not.
 *
 * There is no re-scroll once images load. A late image above the anchor can shift the
 * section down a little; it stays in view.
 */
function decode(hash: string): string {
  try { return decodeURIComponent(hash.slice(1)); } catch { return hash.slice(1); }
}

export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();
  const lastSig = useRef<string | null>(null);
  const navigated = useRef(false);
  useEffect(() => {
    const sig = `${key}|${pathname}|${hash}`;
    if (sig !== lastSig.current) {
      if (lastSig.current !== null) navigated.current = true;
      lastSig.current = sig;
    }
    const mayAnchor = !navigated.current || navType !== 'POP';
    const id = hash ? decode(hash) : '';
    const target = mayAnchor && id ? document.getElementById(id) : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [pathname, hash, key, navType]);
  return null;
}
